import mitraTnaReportApi from '@/api/mitra-tna-report/mitra-tna-report';
import { ROLE_HSE, ROLE_ADMIN_PJO } from '@/constants/mitraRole';
import { APPROVAL_WAITING_STATUS } from '@/constants/mitraFilters';

const state = {
  courses: [],
  courseDetail: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPage: 0
  },
  loading: false,
  error: null,
  // Participant related state
  mandatoriedEmployees: [],
  participants: [],
  participantLoading: false,
  participantError: null,
  // Summary state
  summary: {
    history: {
      submitted: 0,
      approved: 0,
      completed: 0,
      percentage: 0
    },
    this_year: {
      submitted: 0,
      approved: 0,
      completed: 0,
      percentage: 0
    }
  },
  summaryLoading: false,
  summaryError: null
};

const getters = {
  courses: state => state.courses || [],
  courseDetail: state => state.courseDetail || null,
  pagination: state => state.pagination || { total: 0, page: 1, limit: 10, totalPage: 0 },
  loading: state => state.loading || false,
  error: state => state.error || null,
  // Participant getters
  mandatoriedEmployees: state => state.mandatoriedEmployees || [],
  participants: state => state.participants || [],
  participantLoading: state => state.participantLoading || false,
  participantError: state => state.participantError || null,
  // Summary getters
  summary: state => state.summary,
  summaryLoading: state => state.summaryLoading || false,
  summaryError: state => state.summaryError || null
};

const getPartnerTnaApprovalStep = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized.includes('pjo')) return { value: 'pjo', label: 'PJO' };
  if (normalized.includes('admin hse')) return { value: 'hse', label: 'HSE' };
  if (normalized.includes('ktt')) return { value: 'ktt', label: 'KTT' };
  return { value: null, label: status || '-' };
};

const mutations = {
  SET_COURSES(state, courses) {
    state.courses = courses;
  },
  SET_COURSE_DETAIL(state, courseDetail) {
    state.courseDetail = courseDetail;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      total: pagination.total,
      page: pagination.page,
      limit: pagination.limit,
      totalPage: pagination.totalPage
    };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  // Participant mutations
  SET_MANDATORIED_EMPLOYEES(state, employees) {
    state.mandatoriedEmployees = Array.isArray(employees) ? employees : [];
  },
  SET_PARTICIPANTS(state, participants) {
    state.participants = Array.isArray(participants) ? participants : [];
  },
  SET_PARTICIPANT_LOADING(state, loading) {
    state.participantLoading = loading;
  },
  SET_PARTICIPANT_ERROR(state, error) {
    state.participantError = error;
  },
  CLEAR_PARTICIPANT_ERROR(state) {
    state.participantError = null;
  },
  // Summary mutations
  SET_SUMMARY(state, summary) {
    state.summary = {
      history: summary.history || { submitted: 0, approved: 0, completed: 0, percentage: 0 },
      this_year: summary.this_year || { submitted: 0, approved: 0, completed: 0, percentage: 0 }
    };
  },
  SET_SUMMARY_LOADING(state, loading) {
    state.summaryLoading = loading;
  },
  SET_SUMMARY_ERROR(state, error) {
    state.summaryError = error;
  },
  CLEAR_SUMMARY_ERROR(state) {
    state.summaryError = null;
  }
};

const actions = {
  async getCourses({ commit, rootGetters, dispatch }, params) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      // Get user data from users store
      let profileData = rootGetters['users/profileData'];
      
      // Wait for profile to load if empty
      if (!profileData || Object.keys(profileData).length === 0) {
        await dispatch('users/getProfile', null, { root: true });
        profileData = rootGetters['users/profileData'];
      }
      
      const userRoles = profileData?.roles_uenroll || [];

      let response;
      
      // Check if user has PJO role
      if (userRoles.includes(ROLE_ADMIN_PJO)) {
        response = await mitraTnaReportApi.getCoursesPJO(params);
      } 
      // Check if user has HSE role
      else if (userRoles.includes(ROLE_HSE)) {
        response = await mitraTnaReportApi.getCoursesHSE(params);
      } 
      // Default fallback
      else {
        throw new Error('User does not have required role (PJO or HSE)');
      }
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSES', response.data.data.data);
        commit('SET_PAGINATION', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch courses');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getCourseDetail({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraTnaReportApi.getCourseDetail(id);
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSE_DETAIL', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch course detail');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get mandatoried employees for a course
  async getMandatoriedEmployees({ commit }, courseId) {
    commit('SET_PARTICIPANT_LOADING', true);
    commit('CLEAR_PARTICIPANT_ERROR');
    
    try {
      const response = await mitraTnaReportApi.getMandatoriedEmployees(courseId);
      
      if (response.data.httpCode === 200) {
        const employees = response.data.data.map(emp => ({
          ...emp,
          id: emp.nik, // Use NIK as ID
          employee_name: emp.full_name,
          employee_id: emp.nik,
          position: emp.position_level,
          email: emp.email || '-',
          company: emp.company,
          is_mandatory: true // Flag mandatory dari API (tidak bisa dihapus)
        }));
        commit('SET_MANDATORIED_EMPLOYEES', employees);
        return employees;
      } else {
        throw new Error(response.data.message || 'Failed to fetch mandatoried employees');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_PARTICIPANT_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_PARTICIPANT_LOADING', false);
    }
  },

  // Get submitted participants for a course
  async getSubmittedParticipants({ commit, rootGetters }, payload) {
    commit('SET_PARTICIPANT_LOADING', true);
    commit('CLEAR_PARTICIPANT_ERROR');
    
    try {
      const courseId = typeof payload === 'object' ? payload.courseId : payload;
      const params = typeof payload === 'object' ? payload.params : undefined;
      // Get user role to determine which API to use
      const profileData = rootGetters['users/profileData'];
      const userRoles = profileData?.roles_uenroll || [];
      
      let response;

      // Check if user has PJO role
      if (userRoles.includes(ROLE_ADMIN_PJO)) {
        console.log('✅ Participant by PJO');
        response = await mitraTnaReportApi.getSubmittedParticipants(courseId, params);
      } 
      // Check if user has HSE role
      else if (userRoles.includes(ROLE_HSE)) {
        console.log('✅ Participant by HSE');
        response = await mitraTnaReportApi.getSubmittedParticipantsApproveByPJO(courseId, params);
      } 
      else {
        console.error('❌ No valid role found for participant. userRoles:', userRoles);
        throw new Error('User does not have required role (PJO or HSE)');
      }
      
      if (response.data.httpCode === 200) {
        const responsePayload = response.data.data;
        const rows = Array.isArray(responsePayload)
          ? responsePayload
          : responsePayload?.data || [];
        const actionableStatus = userRoles.includes(ROLE_ADMIN_PJO)
          ? APPROVAL_WAITING_STATUS.pjo
          : APPROVAL_WAITING_STATUS.hse;
        const employees = rows.map(emp => {
          const approvalStep = getPartnerTnaApprovalStep(emp.status);
          return {
            ...emp,
            id: emp.id || emp.nik, // Use TNA ID as ID (for approve API)
            tna_id: emp.id, // Store TNA ID separately
            employee_name: emp.full_name,
            employee_id: emp.nik,
            position: emp.position_level,
            email: emp.email || '-',
            company: emp.company,
            backend_status: emp.status || null,
            approval_step: approvalStep.value,
            approval_step_label: approvalStep.label,
            status: emp.status || '-',
            is_actionable: emp.status === actionableStatus,
            is_mandatory: Boolean(emp.is_mandatory),
            training_requirement: emp.is_mandatory ? 'Mandatory' : 'Development'
          };
        });
        return {
          data: employees,
          total: Array.isArray(responsePayload) ? employees.length : Number(responsePayload?.total || 0),
          page: Number(responsePayload?.page || params?.page || 1),
          limit: Number(responsePayload?.limit || params?.limit || employees.length || 25),
          totalPage: Number(responsePayload?.totalPage || 1),
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch submitted participants');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_PARTICIPANT_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_PARTICIPANT_LOADING', false);
    }
  },

  // Approve participants (role-based)
  async approveParticipants({ commit, rootGetters }, { courseId, tnaIds, approveAll = false, tnaPeriodId = null }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      // Get user role to determine which API to use
      const profileData = rootGetters['users/profileData'];
      const userRoles = profileData?.roles_uenroll || [];
      
      console.log('🔍 Debug approveParticipants:', {
        profileData,
        userRoles,
        hasData: Object.keys(profileData || {}).length > 0,
        courseId,
        tnaIds
      });
      
      let response;
      
      // Check if user has PJO role
      if (userRoles.includes(ROLE_ADMIN_PJO)) {
        console.log('✅ Approving by PJO');
        response = await mitraTnaReportApi.approveByPJO(courseId, tnaIds, {
          approveAll,
          ...(tnaPeriodId && { tna_period_id: tnaPeriodId }),
        });
      } 
      // Check if user has HSE role
      else if (userRoles.includes(ROLE_HSE)) {
        console.log('✅ Approving by HSE');
        response = await mitraTnaReportApi.approveByHSE(courseId, tnaIds, {
          approveAll,
          ...(tnaPeriodId && { tna_period_id: tnaPeriodId }),
        });
      } 
      else {
        console.error('❌ No valid role found for approve. userRoles:', userRoles);
        throw new Error('User does not have required role (PJO or HSE)');
      }
      
      if (response.data.httpCode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to approve participants');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Reject participants (role-based)
  async rejectParticipants({ commit, rootGetters }, { courseId, tnaIds }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      // Get user role to determine which API to use
      const profileData = rootGetters['users/profileData'];
      const userRoles = profileData?.roles_uenroll || [];
      
      let response;
      
      // Check if user has PJO role
      if (userRoles.includes(ROLE_ADMIN_PJO)) {
        response = await mitraTnaReportApi.rejectByPJO(courseId, tnaIds);
      } 
      // Check if user has HSE role
      else if (userRoles.includes(ROLE_HSE)) {
        response = await mitraTnaReportApi.rejectByHSE(courseId, tnaIds);
      } 
      else {
        throw new Error('User does not have required role (PJO or HSE)');
      }
      
      if (response.data.httpCode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to reject participants');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get summary statistics (role-based)
  async getSummary({ commit, rootGetters, dispatch }, params) {
    commit('SET_SUMMARY_LOADING', true);
    commit('CLEAR_SUMMARY_ERROR');
    
    try {
      // Get user data from users store
      let profileData = rootGetters['users/profileData'];
      
      // Wait for profile to load if empty
      if (!profileData || Object.keys(profileData).length === 0) {
        console.warn('⚠️ Profile data is empty for summary, dispatching getProfile...');
        await dispatch('users/getProfile', null, { root: true });
        profileData = rootGetters['users/profileData'];
      }
      
      const userRoles = profileData?.roles_uenroll || [];
      
      let response;
      
      // Check if user has PJO role
      if (userRoles.includes(ROLE_ADMIN_PJO)) {
        console.log('✅ Fetching summary for PJO role');
        response = await mitraTnaReportApi.getSummary(params);
      } 
      // Check if user has HSE role
      else if (userRoles.includes(ROLE_HSE)) {
        console.log('✅ Fetching summary for HSE role');
        response = await mitraTnaReportApi.getSummaryHSE(params);
      } 
      // Default fallback
      else {
        console.error('❌ No valid role found for summary. userRoles:', userRoles);
        throw new Error('User does not have required role (PJO or HSE)');
      }
      
      if (response.data.httpCode === 200) {
        commit('SET_SUMMARY', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch summary');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_SUMMARY_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_SUMMARY_LOADING', false);
    }
  },

  // Get companies list
  async getCompanies(_context, params) {
    try {
      const response = await mitraTnaReportApi.getCompanies(params);
      
      if (response.data.httpCode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch companies');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      throw new Error(errorMessage);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
